"use client";

import React, { useEffect, useState } from "react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import { Progress } from "../../ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Checkbox } from "../../ui/checkbox";
import { Input } from "../../ui/input";
import { Database, Play, Loader2, ArrowLeft, AlertTriangle, Search, Upload, Download, CheckCircle, MessageSquare, Save } from "lucide-react";
import { ReviewComment } from "../../shared/ReviewComment";
import { DocumentManager } from "../../planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { 
  getProcedureFileUsageForWorkspace, 
  updateProcedureFileUsageForWorkspace,
  type FileUsageEntry 
} from "@/utils/fileUsageReference";

interface PPEExceptionTestingProps {
  onBack?: () => void;
  setActiveSection?: (section: string) => void;
}


const EXCEPTIONS = [
  { id: 1, description: "Net book value should not be equal to original cost." },
  { id: 2, description: "Useful life cannot be zero or blank." },
  { id: 3, description: "Accumulated depreciation should not exceed original cost." },
  { id: 4, description: "Useful life should not be less than 1 year." },
  { id: 5, description: "Capitalization date mismatch with previous year" },
  { id: 6, description: "Capitalization date must lie within current FY" },
];

export default function PPEExceptionTesting({ onBack, setActiveSection }: PPEExceptionTestingProps) {
  const handleBack = () => {
    if (setActiveSection) {
      setActiveSection("ppe-substantive");
    } else if (onBack) {
      onBack();
    }
  };
  const [currentFarFile, setCurrentFarFile] = useState<string>("");
  const [previousFarFile, setPreviousFarFile] = useState<string>("");
  const [selectedExceptions, setSelectedExceptions] = useState<number[]>([]);
  const [processingStatus, setProcessingStatus] = useState<"idle" | "running" | "completed" | "error">("idle");
  const [progress, setProgress] = useState(0);
  const [fileUrl, setFileUrl] = useState<string>("");
  
  // File search states (shared for both current and previous)
  const [fileSearchStatus, setFileSearchStatus] = useState<"idle" | "searching" | "found" | "not_found">("idle");
  const [foundFiles, setFoundFiles] = useState<string[]>([]);
  const [currentReferenceValue, setCurrentReferenceValue] = useState<string>("");
  const [currentUploadStatus, setCurrentUploadStatus] = useState<"idle" | "uploading" | "completed" | "error">("idle");
  const [previousReferenceValue, setPreviousReferenceValue] = useState<string>("");
  const [previousUploadStatus, setPreviousUploadStatus] = useState<"idle" | "uploading" | "completed" | "error">("idle");
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  const [fileUsageReference, setFileUsageReference] = useState<FileUsageEntry[]>([]);
  const [companyName, setCompanyName] = useState<string>("");
  const [inferenceQuestion, setInferenceQuestion] = useState<any>({ textResponse: null, documents: [] });
  const [inferenceSaveStatus, setInferenceSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [inferenceSaveError, setInferenceSaveError] = useState<string | null>(null);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `PPE_EXCEPTION_TESTING_${normalizedSection}`;
  };
  
  const loadFileUsageReference = async () => {
    try {
      const usage = await getProcedureFileUsageForWorkspace("Property, Plant & Equipment", "Exception Testing");
      setFileUsageReference(usage);
    } catch (error) {
      console.error("Error loading file usage reference:", error);
    }
  };

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load inference question from Execution_PPE_Responses.json
  useEffect(() => {
    const loadInferenceQuestion = async () => {
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.("Execution_PPE_Responses.json");
        if (result?.success && result?.data?.substantiveProcedures) {
          const inferenceId = "EXCEPTION-TESTING-PPE-INFERENCE";
          const inferenceData = result.data.substantiveProcedures[inferenceId];
          if (inferenceData) {
            setInferenceQuestion({
              textResponse: inferenceData.textResponse || null,
              documents: (inferenceData.documents || []).map((docName: string) => ({
                id: `doc_${docName}`,
                type: 'linked',
                name: docName,
              })),
            });
          }
        }
      } catch (error) {
        console.warn("Failed to load inference question:", error);
      }
    };
    loadInferenceQuestion();
  }, []);

  useEffect(() => {
    // On mount, mimic pressing Search for Files and Load Exceptions
    searchForFiles();
    loadFileUsageReference();
    // Load previously saved exception selections for PPE
    if (window && (window as any).sharePointAPI) {
      (async () => {
        try {
          if (window.sharePointAPI?.loadExceptionList) {
            const result = await window.sharePointAPI.loadExceptionList("ppe");
            if (result?.success && Array.isArray(result.data)) {
              setSelectedExceptions(result.data as number[]);
            }
          }
        } catch {}
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "completed" | "error">("idle");

  const searchForFiles = async () => {
    setFileSearchStatus("searching");
    try {
      if (window.sharePointAPI?.loadComcFiles) {
        const result = await window.sharePointAPI.loadComcFiles("Execution_PPE_FAR");
        if (result.success && result.data?.files) {
          const files = result.data.files
            .map((f: any) => f.name)
            .filter((name: string) => name && name.trim().length > 0);
          if (files.length > 0) {
            setFoundFiles(files);
            setFileSearchStatus("found");
          } else {
            setFileSearchStatus("not_found");
          }
        } else {
          setFileSearchStatus("not_found");
        }
      } else {
        setFileSearchStatus("not_found");
      }
    } catch {
      setFileSearchStatus("not_found");
    }
  };

  const uploadCurrentFile = async () => {
    setCurrentUploadStatus("uploading");
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.xlsx,.xls,.csv';
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          try {
            const base64Content = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result = reader.result as string;
                const base64 = result.split(',')[1];
                resolve(base64);
              };
              reader.onerror = () => reject(new Error('Failed to read file'));
              reader.readAsDataURL(file);
            });

            if (window.sharePointAPI?.uploadFile) {
              const result = await window.sharePointAPI.uploadFile({
                fileContent: base64Content,
                fileName: file.name,
                referenceValue: currentReferenceValue,
                folderName: "client",
                fyYear: getFY_YEAR(),
                sectionList: ["Execution_PPE_FAR"],
              });

              if (result.success) {
                setCurrentFarFile(file.name);
                setFoundFiles([file.name]);
                setFileSearchStatus("found");
                setCurrentUploadStatus("completed");
              } else {
                setCurrentUploadStatus("error");
              }
            } else {
              setCurrentUploadStatus("error");
            }
          } catch {
            setCurrentUploadStatus("error");
          }
        }
      };
      input.click();
    } catch {
      setCurrentUploadStatus("error");
    }
  };

  const uploadPreviousFile = async () => {
    setPreviousUploadStatus("uploading");
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.xlsx,.xls,.csv';
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          try {
            const base64Content = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result = reader.result as string;
                const base64 = result.split(',')[1];
                resolve(base64);
              };
              reader.onerror = () => reject(new Error('Failed to read file'));
              reader.readAsDataURL(file);
            });

            if (window.sharePointAPI?.uploadFile) {
              const result = await window.sharePointAPI.uploadFile({
                fileContent: base64Content,
                fileName: file.name,
                referenceValue: previousReferenceValue,
                folderName: "client",
                fyYear: getFY_YEAR(),
                sectionList: ["Execution_PPE_FAR"],
              });

              if (result.success) {
                setPreviousFarFile(file.name);
                setFoundFiles([file.name]);
                setFileSearchStatus("found");
                setPreviousUploadStatus("completed");
              } else {
                setPreviousUploadStatus("error");
              }
            } else {
              setPreviousUploadStatus("error");
            }
          } catch {
            setPreviousUploadStatus("error");
          }
        }
      };
      input.click();
    } catch {
      setPreviousUploadStatus("error");
    }
  };

  const handleExceptionToggle = (exceptionId: number) => {
    setSelectedExceptions(prev => prev.includes(exceptionId) ? prev.filter(id => id !== exceptionId) : [...prev, exceptionId]);
  };

  const handleSaveExceptions = async () => {
    if (selectedExceptions.length === 0) {
      alert("Please select at least one exception to save");
      return;
    }

    setSaveStatus("saving");
    try {
      if (!window.sharePointAPI?.saveExceptionList) {
        throw new Error("SharePoint API method 'saveExceptionList' is not available. Please restart the application.");
      }

      const result = await window.sharePointAPI.saveExceptionList(selectedExceptions, "ppe");

      if (result.success) {
        setSaveStatus("completed");
        alert(`Successfully saved ${selectedExceptions.length} exception(s) to Exception_Test.json`);
        setTimeout(() => setSaveStatus("idle"), 3000);
      } else {
        setSaveStatus("error");
        alert(`Failed to save exceptions: ${result.error || "Unknown error"}`);
      }
    } catch (error) {
      setSaveStatus("error");
      console.error("Error saving exceptions:", error);
      alert(`Failed to save exceptions: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  };

  const handleLoadExceptions = async () => {
    try {
      if (!window.sharePointAPI?.loadExceptionList) {
        alert("SharePoint API method 'loadExceptionList' is not available. Please restart the application.");
        return;
      }
      const result = await window.sharePointAPI.loadExceptionList("ppe");
      if (result.success) {
        const selectedIds = Array.isArray(result.data) ? result.data : [];
        setSelectedExceptions(selectedIds);
        alert(`Loaded ${selectedIds.length} exception(s) from Exception_Test.json`);
      } else {
        alert(`Failed to load exceptions: ${result.error || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Error loading exceptions:", error);
      alert(`Failed to load exceptions: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  };

  const canRunExceptionTests = () => currentFarFile && previousFarFile && selectedExceptions.length > 0;

  const runExceptionTests = async () => {
    if (!canRunExceptionTests()) return;
    
    // Save file names to file usage reference
    const entries: FileUsageEntry[] = [];
    const files: string[] = [];
    if (currentFarFile) {
      const fileName = currentFarFile.includes(' (') ? currentFarFile.split(' (')[0] : currentFarFile;
      files.push(fileName);
    }
    if (previousFarFile) {
      const fileName = previousFarFile.includes(' (') ? previousFarFile.split(' (')[0] : previousFarFile;
      files.push(fileName);
    }
    if (files.length > 0) {
      entries.push({ "Search for Files": files.join(', ') });
      await updateProcedureFileUsageForWorkspace("Property, Plant & Equipment", "Exception Testing", entries);
      await loadFileUsageReference();
    }
    
    // Trigger the same functionality as "Save Exceptions" before running tests
    try {
      await handleSaveExceptions();
    } catch (e) {
      console.warn("Saving exceptions before run failed, continuing with run", e);
    }
    
    setProcessingStatus("running");
    setProgress(0);
    setFileUrl("");

    try {
      if (window.payroll?.run) {
        const result = await window.payroll.run("execute_ppe_exception_testing", {
          inputFiles: [],
          options: {
            current_far_file: currentFarFile,
            previous_far_file: previousFarFile,
            exception_numbers: selectedExceptions,
          },
        });

        if (result.ok) {
          setProcessingStatus("completed");
        } else {
          setProcessingStatus("error");
        }
      } else {
        setProcessingStatus("error");
      }
    } catch (error) {
      console.error("Error running PPE Exception Testing:", error);
      setProcessingStatus("error");
    }
  };

  // Save inference question
  const saveInferenceQuestion = async () => {
    if (!companyName) {
      setInferenceSaveError("Company name is required");
      setInferenceSaveStatus('error');
      return;
    }

    setInferenceSaveStatus('saving');
    setInferenceSaveError(null);

    try {
      // Auto-save any pending textbox content
      const documentManagers = document.querySelectorAll('[data-document-manager]');
      for (const manager of Array.from(documentManagers)) {
        const itemId = manager.getAttribute('data-item-id');
        if (itemId) {
          const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
          if (autoSaveMethod && typeof autoSaveMethod === 'function') {
            autoSaveMethod();
          }
        }
      }

      await new Promise(resolve => setTimeout(resolve, 1500));

      const areaPrefix = "PPE";
      const fileName = `Execution_${areaPrefix}_Responses.json`;
      
      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        }
      } catch (error) {
        console.warn(`Failed to download existing ${fileName}, starting from empty`, error);
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Save inference question - query db.json for section field like CustomProcedure
      const inferenceId = "EXCEPTION-TESTING-PPE-INFERENCE";
      const sectionToFind = `Execution_${areaPrefix}_${inferenceId}`;
      let inferenceDocumentNames: string[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            inferenceDocumentNames = dbResult.data
              .map((file: any) => file.name || '')
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch (error) {
        console.warn(`Failed to query db.json for section ${sectionToFind}:`, error);
      }

      const inferenceTextboxFromLocalStorage = localStorage.getItem(`textbox_${inferenceId}`);
      const hasInferenceTextboxContent = inferenceTextboxFromLocalStorage && inferenceTextboxFromLocalStorage.trim().length > 0;
      
      let inferenceTextResponseData = null;
      if (hasInferenceTextboxContent) {
        inferenceTextResponseData = {
          html: inferenceTextboxFromLocalStorage,
          plainText: inferenceTextboxFromLocalStorage.replace(/<[^>]*>/g, '')
        };
      } else if (inferenceQuestion.textResponse) {
        inferenceTextResponseData = inferenceQuestion.textResponse;
      }

      existingData.substantiveProcedures[inferenceId] = {
        selectedOption: "",
        textResponse: inferenceTextResponseData,
        documents: inferenceDocumentNames,
        table: null,
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `Execution ${areaPrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: fyYear,
        sectionList: [`Execution_${areaPrefix}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save inference question");
      }

      setInferenceSaveStatus('saved');
      setTimeout(() => setInferenceSaveStatus('idle'), 3000);
    } catch (error) {
      console.error("Failed to save inference question:", error);
      setInferenceSaveError(error instanceof Error ? error.message : "Unknown error");
      setInferenceSaveStatus('error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Exception Testing - Property, Plant & Equipment</h2>
          <p className="text-gray-400">Run exception tests on FAR files and generate exception reports</p>
        </div>
        <Button variant="outline" onClick={handleBack} className="border-white/10 text-white/80 hover:bg-white/10 hover:text-white">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
      </div>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Search className="h-5 w-5 text-blue-500" />
              Step 1: Search for Existing Files
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 1: Search for Existing Files"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* File Search Status */}
          <div className="flex items-center gap-3">
            {fileSearchStatus === "searching" && (
              <div className="flex items-center gap-2 text-blue-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Searching for files...</span>
              </div>
            )}
            {fileSearchStatus === "found" && (
              <span className="text-green-400 font-bold"> FOUND!</span>
            )}
            {fileSearchStatus === "not_found" && (
              <span className="text-yellow-400">No files found</span>
            )}
          </div>

          {/* File Selection Dropdowns */}
          {fileSearchStatus === "found" && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-white">Current FAR File</Label>
                <Select value={currentFarFile} onValueChange={setCurrentFarFile}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select current FAR file" />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">
                    {foundFiles.map((f) => (
                      <SelectItem key={f} value={f}>
                        {f}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fileUsageReference.find(e => e["Search for Files"]) && (() => {
                  const files = fileUsageReference.find(e => e["Search for Files"])?.["Search for Files"] || "";
                  const fileList = files.split(', ').filter(f => f);
                  const currentFile = fileList[0] || "";
                  return currentFile ? (
                    <p className="text-xs text-white/60 mt-1">
                      Last used: {currentFile}
                    </p>
                  ) : null;
                })()}
              </div>
              <div className="space-y-2">
                <Label className="text-white">Previous FAR File</Label>
                <Select value={previousFarFile} onValueChange={setPreviousFarFile}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select previous FAR file" />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">
                    {foundFiles.map((f) => (
                      <SelectItem key={f} value={f}>
                        {f}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fileUsageReference.find(e => e["Search for Files"]) && (() => {
                  const files = fileUsageReference.find(e => e["Search for Files"])?.["Search for Files"] || "";
                  const fileList = files.split(', ').filter(f => f);
                  const previousFile = fileList[1] || "";
                  return previousFile ? (
                    <p className="text-xs text-white/60 mt-1">
                      Last used: {previousFile}
                    </p>
                  ) : null;
                })()}
              </div>
            </div>
          )}

          {/* Upload Section when no files found */}
          {fileSearchStatus === "not_found" && (
            <div className="space-y-4 p-4 border border-yellow-500/20 bg-yellow-500/5 rounded-lg">
              <div className="text-yellow-400 font-medium">No files found. Please upload files.</div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Current FAR File Upload */}
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-white">Current FAR File Reference (optional)</Label>
                    <Input
                      value={currentReferenceValue}
                      onChange={(e) => setCurrentReferenceValue(e.target.value)}
                      placeholder="Enter reference value"
                      className="border-white/10 bg-black/40 text-white"
                    />
                  </div>
                  <Button onClick={uploadCurrentFile} disabled={currentUploadStatus === "uploading"} className="bg-green-600 hover:bg-green-700 text-white">
                    {currentUploadStatus === "uploading" ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                    ) : (
                      <><Upload className="h-4 w-4 mr-2" /> Upload Current FAR File</>
                    )}
                  </Button>
                  {currentUploadStatus === "completed" && (
                    <span className="text-green-400 text-sm">Current FAR file uploaded successfully!</span>
                  )}
                  {currentUploadStatus === "error" && (
                    <span className="text-red-400 text-sm">Upload failed</span>
                  )}
                </div>

                {/* Previous FAR File Upload */}
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-white">Previous FAR File Reference (optional)</Label>
                    <Input
                      value={previousReferenceValue}
                      onChange={(e) => setPreviousReferenceValue(e.target.value)}
                      placeholder="Enter reference value"
                      className="border-white/10 bg-black/40 text-white"
                    />
                  </div>
                  <Button onClick={uploadPreviousFile} disabled={previousUploadStatus === "uploading"} className="bg-green-600 hover:bg-green-700 text-white">
                    {previousUploadStatus === "uploading" ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                    ) : (
                      <><Upload className="h-4 w-4 mr-2" /> Upload Previous FAR File</>
                    )}
                  </Button>
                  {previousUploadStatus === "completed" && (
                    <span className="text-green-400 text-sm">Previous FAR file uploaded successfully!</span>
                  )}
                  {previousUploadStatus === "error" && (
                    <span className="text-red-400 text-sm">Upload failed</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-white">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5" />
              Exception Tests
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Exception Tests"))}
                className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                title="Review Comment"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const allSelected = selectedExceptions.length === EXCEPTIONS.length;
                  setSelectedExceptions(allSelected ? [] : EXCEPTIONS.map(e => e.id));
                }}
                className="border-white/10"
              >
                {selectedExceptions.length === EXCEPTIONS.length ? "Deselect All" : "Select All"}
              </Button>
              <span className="rounded bg-blue-500/20 px-2 py-1 text-xs text-blue-300">
                {selectedExceptions.length} selected
              </span>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded border border-white/10 bg-white/5">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20 text-white">Sl. No.</TableHead>
                  <TableHead className="text-white">Exception</TableHead>
                  <TableHead className="w-40 text-white">Selection</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {EXCEPTIONS.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="text-white">{e.id}</TableCell>
                    <TableCell className="text-white">{e.description}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`exception-${e.id}`}
                          checked={selectedExceptions.includes(e.id)}
                          onCheckedChange={() => handleExceptionToggle(e.id)}
                        />
                        <Label htmlFor={`exception-${e.id}`} className="text-white">
                          {selectedExceptions.includes(e.id) ? "Selected" : "Select"}
                        </Label>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Play className="h-5 w-5" />
              Execute Exception Tests
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Execute Exception Tests"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">
                Run selected exception tests on the FAR files
              </p>
              {!canRunExceptionTests() && (
                <p className="mt-1 text-xs text-red-400">
                  Please select at least one exception test and choose the FAR files
                </p>
              )}
            </div>
            <Button
              onClick={runExceptionTests}
              disabled={!canRunExceptionTests() || processingStatus === "running"}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50"
            >
              {processingStatus === "running" ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Running Tests...
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  Run Exception Tests
                </>
              )}
            </Button>
          </div>

          {/* Processing Indicator */}
          {processingStatus === "running" && (
            <div className="flex items-center gap-2 text-blue-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Processing Exception Tests...</span>
            </div>
          )}

          {/* Results Summary */}
          {processingStatus === "completed" && (
            <div className="space-y-4">
              <div className="rounded border border-green-500/20 bg-green-500/10 p-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <div>
                    <h4 className="font-medium text-green-300">Exception Testing Completed</h4>
                    <p className="text-sm text-green-200">
                      All selected tests have been executed successfully
                    </p>
                  </div>
                </div>
              </div>

              {fileUrl && (
                <div className="flex items-center gap-4">
                  <Button variant="outline" className="border-green-500/30 text-green-300">
                    <Download className="mr-2 h-4 w-4" />
                    Download Excel Report
                  </Button>
                  <a
                    href={fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 underline text-sm"
                  >
                    Open Exception Testing
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Error State */}
          {processingStatus === "error" && (
            <div className="rounded border border-red-500/20 bg-red-500/10 p-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                <div>
                  <h4 className="font-medium text-red-300">Exception Testing Failed</h4>
                  <p className="text-sm text-red-200">
                    An error occurred while running the exception tests. Please check your data and try again.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Inference and Conclusion Question */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Document the inference and conclusion from the above procedure performed.</CardTitle>
            <div className="flex items-center gap-2">
              {inferenceSaveStatus === 'saved' && (
                <span className="text-sm text-green-400">Saved!</span>
              )}
              {inferenceSaveStatus === 'error' && inferenceSaveError && (
                <span className="text-sm text-red-400">Error</span>
              )}
              <Button
                onClick={saveInferenceQuestion}
                disabled={inferenceSaveStatus === 'saving' || !companyName}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              >
                {inferenceSaveStatus === 'saving' ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DocumentManager
            sectionKey="ppe"
            itemId="EXCEPTION-TESTING-PPE-INFERENCE"
            itemQuestion="Document the inference and conclusion from the above procedure performed."
            itemTextResponse={inferenceQuestion.textResponse}
            itemDocuments={inferenceQuestion.documents || []}
            onTextResponseChange={(textResponse) => setInferenceQuestion({ ...inferenceQuestion, textResponse })}
            onDocumentsChange={(documents) => setInferenceQuestion({ ...inferenceQuestion, documents })}
            disabled={false}
          />
        </CardContent>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Step 1: Search for Existing Files")]: "Step 1: Search for Existing Files",
              [getSectionQuestionId("Exception Tests")]: "Exception Tests",
              [getSectionQuestionId("Execute Exception Tests")]: "Execute Exception Tests",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}
